import React from 'react';
import Header from '../components/Header';
import SuccessStoriesSection from '../components/SuccessStoriesSection';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { AlertCircle, TrendingUp } from 'lucide-react';

const StudentSuccessPage = () => {
  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-20 pb-12 bg-gradient-to-br from-indigo-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <span className="bg-indigo-100 text-indigo-700 px-6 py-2 rounded-full text-sm font-semibold">STUDENT SUCCESS</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">Success Stories & Lessons Learned</h1>
          <p className="text-xl text-slate-600 font-light max-w-3xl mx-auto">Real transformations, proven strategies, and the pitfalls to avoid on your journey to career success</p>
        </div>
      </section>

      {/* Success Stories Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">Real People, Real Transformations</h2>
          <p className="text-xl text-slate-600">See how our students went from where you are now to where you want to be</p>
        </div>
        <SuccessStoriesSection />
      </section>

      {/* Common Pitfalls */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-4xl font-bold text-slate-900 mb-4">Common Pitfalls to Avoid</h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">Learn from others' mistakes so you don't have to make them yourself</p>
          </div>

          <div className="space-y-8">
            <div className="bg-white rounded-3xl p-8 border-l-4 border-amber-500 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-amber-700 font-bold text-lg">1</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Lack of Clear Direction</h3>
                  <p className="text-slate-700 mb-4">Jumping between courses without a roadmap leads to scattered learning and no cohesive expertise.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                      <p className="text-red-800 font-semibold mb-1">❌ What Happens</p>
                      <p className="text-sm text-slate-600">6 months of random learning → No job offers → Frustration</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                      <p className="text-green-800 font-semibold mb-1">✅ Our Solution</p>
                      <p className="text-sm text-slate-600">Clear 4-step framework from beginner to employed professional</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border-l-4 border-amber-500 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-amber-700 font-bold text-lg">2</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Learning Without Accountability</h3>
                  <p className="text-slate-700 mb-4">Self-paced online courses sound great, but 95% of people never finish without deadlines and support.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                      <p className="text-red-800 font-semibold mb-1">❌ What Happens</p>
                      <p className="text-sm text-slate-600">Start motivated → Life gets busy → Never complete → Wasted money</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                      <p className="text-green-800 font-semibold mb-1">✅ Our Solution</p>
                      <p className="text-sm text-slate-600">Live sessions, cohort learning, and mentor check-ins keep you on track</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border-l-4 border-amber-500 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-amber-700 font-bold text-lg">3</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Theory Without Practice</h3>
                  <p className="text-slate-700 mb-4">Watching tutorials and taking notes doesn't equal real skills. Employers want proven ability.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                      <p className="text-red-800 font-semibold mb-1">❌ What Happens</p>
                      <p className="text-sm text-slate-600">Hours of videos → Can't build anything → Fail technical interviews</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                      <p className="text-green-800 font-semibold mb-1">✅ Our Solution</p>
                      <p className="text-sm text-slate-600">Build 5+ portfolio projects with mentor guidance and feedback</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-3xl p-8 border-l-4 border-amber-500 shadow-sm">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-amber-700 font-bold text-lg">4</span>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 mb-2">Procrastination & "Waiting for the Perfect Time"</h3>
                  <p className="text-slate-700 mb-4">Researching endlessly, overthinking, waiting for the "right moment" - meanwhile, opportunities pass by.</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                      <p className="text-red-800 font-semibold mb-1">❌ What Happens</p>
                      <p className="text-sm text-slate-600">"I'll start next month" → 6 months later → Still haven't started</p>
                    </div>
                    <div className="bg-green-50 p-4 rounded-xl border border-green-200">
                      <p className="text-green-800 font-semibold mb-1">✅ Our Solution</p>
                      <p className="text-sm text-slate-600">Fixed cohort start dates and enrollment deadlines create urgency</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Difference Maker */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <TrendingUp className="w-16 h-16 text-indigo-600 mx-auto mb-6" />
          <h2 className="text-4xl font-bold text-slate-900 mb-6">What Makes the Difference?</h2>
          <div className="bg-indigo-50 rounded-3xl p-8 mb-8">
            <p className="text-xl text-slate-700 leading-relaxed mb-6">
              The students who succeed don't have more talent or more time. They have the right structure, support, and accountability.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-left">
              <div className="bg-white p-6 rounded-xl">
                <div className="text-indigo-600 font-bold text-lg mb-2">Clear Roadmap</div>
                <p className="text-slate-600 text-sm">Know exactly what to learn, when, and why</p>
              </div>
              <div className="bg-white p-6 rounded-xl">
                <div className="text-indigo-600 font-bold text-lg mb-2">Expert Guidance</div>
                <p className="text-slate-600 text-sm">Mentor support when you're stuck or confused</p>
              </div>
              <div className="bg-white p-6 rounded-xl">
                <div className="text-indigo-600 font-bold text-lg mb-2">Accountability</div>
                <p className="text-slate-600 text-sm">Deadlines, cohorts, and check-ins keep momentum</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-indigo-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">Ready to Write Your Success Story?</h2>
          <p className="text-xl text-indigo-100 font-light mb-8">Join hundreds of students who transformed their careers with the right structure and support.</p>
          <Button size="lg" className="bg-white text-indigo-700 hover:bg-gray-100 font-semibold px-10 py-6 text-lg rounded-md" onClick={() => window.location.href = '/contact'}>
            Schedule Your Free Consultation
          </Button>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default StudentSuccessPage;
